import { useState } from 'react';
import { AlertTriangle, Bell, CheckCircle, Plus, Server, Trash2, Edit2 } from 'lucide-react';
import { useServers } from '../../hooks/useServers';
import { useActiveAlerts, useAlertHistory, useAlertRules, useCreateAlertRule, useDeleteAlertRule, useUpdateAlertRule } from '../../hooks/useAlerts';
import { Button } from '../../components/ui/Button';
import { AlertRule } from '../../lib/api/alerts';
import { useToastStore } from '../../stores/toastStore';

const METRIC_LABELS: Record<string, string> = { cpu: 'CPU', memory: 'Memory', disk: 'Disk' };
const METRIC_UNITS: Record<string, string> = { cpu: '%', memory: '%', disk: '%' };

function metricColor(metric: string) {
  return metric === 'cpu' ? 'text-blue-600 bg-blue-50' : metric === 'memory' ? 'text-purple-600 bg-purple-50' : 'text-orange-600 bg-orange-50';
}

function AlertRuleRow({ rule, onEdit, onDelete }: { rule: AlertRule; onEdit: (r: AlertRule) => void; onDelete: (id: string) => void }) {
  return (
    <div className="flex items-center justify-between py-3 px-4 bg-white rounded-lg border border-gray-200">
      <div className="flex items-center gap-3">
        <span className={`text-xs font-semibold px-2 py-1 rounded ${metricColor(rule.metric)}`}>
          {METRIC_LABELS[rule.metric]}
        </span>
        <span className="text-sm text-gray-700">
          Alert when &gt; <strong>{rule.threshold}{METRIC_UNITS[rule.metric]}</strong>
        </span>
        {rule.notify_email && (
          <span className="text-xs text-gray-400">→ {rule.notify_email}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${rule.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
          {rule.enabled ? 'Active' : 'Disabled'}
        </span>
        <button onClick={() => onEdit(rule)} className="p-1 text-gray-400 hover:text-gray-600">
          <Edit2 className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => onDelete(rule.id)} className="p-1 text-gray-400 hover:text-red-500">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function RuleFormModal({
  serverId,
  editRule,
  onClose,
}: {
  serverId: string;
  editRule: AlertRule | null;
  onClose: () => void;
}) {
  const [metric, setMetric] = useState<'cpu' | 'memory' | 'disk'>(editRule?.metric ?? 'cpu');
  const [threshold, setThreshold] = useState(editRule?.threshold?.toString() ?? '80');
  const [enabled, setEnabled] = useState(editRule?.enabled ?? true);
  const [notifyEmail, setNotifyEmail] = useState(editRule?.notify_email ?? '');
  const { addToast } = useToastStore();
  const create = useCreateAlertRule(serverId);
  const update = useUpdateAlertRule(serverId);

  const saving = create.isPending || update.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const data = { metric, threshold: parseFloat(threshold), enabled, notify_email: notifyEmail || null };
    try {
      if (editRule) {
        await update.mutateAsync({ ruleId: editRule.id, data });
        addToast({ type: 'success', message: 'Alert rule updated' });
      } else {
        await create.mutateAsync(data);
        addToast({ type: 'success', message: 'Alert rule created' });
      }
      onClose();
    } catch {
      addToast({ type: 'error', message: 'Failed to save alert rule' });
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {editRule ? 'Edit Alert Rule' : 'New Alert Rule'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Metric</label>
            <select
              value={metric}
              onChange={(e) => setMetric(e.target.value as 'cpu' | 'memory' | 'disk')}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="cpu">CPU Usage</option>
              <option value="memory">Memory Usage</option>
              <option value="disk">Disk Usage</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Threshold (%) — alert fires above this value
            </label>
            <input
              type="number"
              min="1"
              max="100"
              step="1"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notify email (optional)</label>
            <input
              type="email"
              value={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.value)}
              placeholder="alerts@example.com"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="enabled"
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-4 w-4 text-primary-600 rounded border-gray-300"
            />
            <label htmlFor="enabled" className="text-sm text-gray-700">Rule enabled</label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : editRule ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ServerAlertSection({ serverId, serverName }: { serverId: string; serverName: string }) {
  const { data: rules = [], isLoading } = useAlertRules(serverId);
  const deleteRule = useDeleteAlertRule(serverId);
  const { addToast } = useToastStore();
  const [showModal, setShowModal] = useState(false);
  const [editRule, setEditRule] = useState<AlertRule | null>(null);

  async function handleDelete(ruleId: string) {
    try {
      await deleteRule.mutateAsync(ruleId);
      addToast({ type: 'success', message: 'Alert rule deleted' });
    } catch {
      addToast({ type: 'error', message: 'Failed to delete rule' });
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2">
          <Server className="h-4 w-4 text-gray-500" />
          <span className="font-medium text-gray-800 text-sm">{serverName}</span>
          <span className="text-xs text-gray-400">({rules.length} rule{rules.length !== 1 ? 's' : ''})</span>
        </div>
        <Button size="sm" variant="outline" onClick={() => { setEditRule(null); setShowModal(true); }}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add Rule
        </Button>
      </div>
      <div className="p-3 space-y-2">
        {isLoading ? (
          <div className="text-sm text-gray-400 py-2 px-1">Loading…</div>
        ) : rules.length === 0 ? (
          <div className="text-sm text-gray-400 py-3 text-center">No alert rules configured</div>
        ) : (
          rules.map((rule) => (
            <AlertRuleRow
              key={rule.id}
              rule={rule}
              onEdit={(r) => { setEditRule(r); setShowModal(true); }}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>
      {showModal && (
        <RuleFormModal
          serverId={serverId}
          editRule={editRule}
          onClose={() => { setShowModal(false); setEditRule(null); }}
        />
      )}
    </div>
  );
}

export function MonitoringPage() {
  const [tab, setTab] = useState<'rules' | 'active' | 'history'>('active');
  const { data: serversData } = useServers({ limit: 100 });
  const servers = serversData?.items ?? [];
  const { data: activeAlerts = [], isLoading: loadingActive } = useActiveAlerts();
  const { data: historyAlerts = [], isLoading: loadingHistory } = useAlertHistory();

  const serverMap = Object.fromEntries(servers.map((s) => [s.id, s.name]));

  function AlertRow({ alert }: { alert: ReturnType<typeof activeAlerts[0]['valueOf']> }) {
    const a = alert as typeof activeAlerts[0];
    return (
      <div className={`flex items-center justify-between py-3 px-4 rounded-lg border ${a.status === 'active' ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}>
        <div className="flex items-center gap-3">
          {a.status === 'active'
            ? <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
            : <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />}
          <div>
            <div className="text-sm font-medium text-gray-900">
              {serverMap[a.server_id] ?? a.server_id} — {METRIC_LABELS[a.metric]} at <strong>{a.current_value.toFixed(1)}{METRIC_UNITS[a.metric]}</strong>
            </div>
            <div className="text-xs text-gray-500">Threshold: {a.threshold}{METRIC_UNITS[a.metric]}</div>
          </div>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${a.status === 'active' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
          {a.status}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Monitoring & Alerts</h1>
          <p className="text-gray-500">Configure alert rules and view active alerts</p>
        </div>
        {activeAlerts.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
            <Bell className="h-4 w-4 text-red-500" />
            <span className="text-sm font-medium text-red-700">{activeAlerts.length} active alert{activeAlerts.length !== 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {([['active', 'Active Alerts'], ['history', 'Alert History'], ['rules', 'Alert Rules']] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === key
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
            {key === 'active' && activeAlerts.length > 0 && (
              <span className="ml-1.5 text-xs bg-red-500 text-white px-1.5 py-0.5 rounded-full">{activeAlerts.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Active Alerts */}
      {tab === 'active' && (
        <div className="space-y-2">
          {loadingActive ? (
            <div className="text-sm text-gray-400 py-4 text-center">Loading alerts…</div>
          ) : activeAlerts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">All clear</h3>
              <p className="text-gray-500 mt-1">No active alerts right now</p>
            </div>
          ) : (
            activeAlerts.map((a) => <AlertRow key={a.id} alert={a} />)
          )}
        </div>
      )}

      {/* History */}
      {tab === 'history' && (
        <div className="space-y-2">
          {loadingHistory ? (
            <div className="text-sm text-gray-400 py-4 text-center">Loading history…</div>
          ) : historyAlerts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <AlertTriangle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No alert history</h3>
              <p className="text-gray-500 mt-1">Resolved alerts will appear here</p>
            </div>
          ) : (
            historyAlerts.map((a) => <AlertRow key={a.id} alert={a} />)
          )}
        </div>
      )}

      {/* Alert Rules */}
      {tab === 'rules' && (
        <div className="space-y-4">
          {servers.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <Server className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No servers</h3>
              <p className="text-gray-500 mt-1">Add a server first to configure alert rules</p>
            </div>
          ) : (
            servers.map((s) => (
              <ServerAlertSection key={s.id} serverId={s.id} serverName={s.name} />
            ))
          )}
        </div>
      )}
    </div>
  );
}
